import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, Breadcrumbs, FAQ, CTABand } from "@/components/site/Blocks";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { DscrRateCenter } from "@/components/rates/DscrRateCenter";
import {
  RateComparisonExplainer,
  RATE_EXPLAINER_HEADING,
  RATE_EXPLAINER_INTRO,
} from "@/components/rates/RateExplainer";
import { RateEducation } from "@/components/rates/RateEducation";

import { buildHead } from "@/lib/seo-head";
import { getDscrRateSnapshot } from "@/lib/rate-center.functions";
import {
  DEFAULT_RATE_SCENARIO,
  emptyRateResponse,
  scenarioKey,
  type PublicRateResponse,
} from "@/lib/rate-center";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import investmentPropertyRatesHero from "@/assets/hero/hero-investment-property-rates-desktop-office-v2.jpg";


const URL = "/investment-property-rates";

const KEYWORDS = [
  "investment property rates",
  "investment property loan rates",
  "DSCR loan rates today",
  "rental property mortgage rates",
  "DSCR rate quote",
  "live DSCR pricing",
  "investor loan APR",
  "30 year fixed DSCR rate",
  "DSCR points and lender credit",
  "rate lock investment property",
  "LLC rental property loan rates",
  "short term rental loan rates",
  "cash out refinance investment property rates",
  "no income verification rental loan rates",
].join(", ");

const FAQS = [
  {
    q: "Are these investment property rates live?",
    a: "Yes. Each option is priced through our lender pricing engine for the exact scenario shown and stamped with the time it was priced. Pricing changes intraday, so the figures are indications rather than a locked rate.",
  },
  {
    q: "Why do you show APR next to every rate?",
    a: "APR reflects financing cost over the loan term, including pricing adjustments the note rate alone does not show. For each option we publish either the APR returned with that quote or our own audited APR calculation, used only where a verified borrower-paid lender fee sits outside the quoted price. We never estimate an APR from unverified figures.",
  },
  {
    q: "What is the difference between borrower points and a lender credit?",
    a: "Points are money you pay at closing to reduce the rate. A lender credit is money the lender pays toward your closing costs in exchange for a higher rate. We display them separately and never net them into a single number.",
  },
  {
    q: "What does 'Par pricing' mean?",
    a: "Par means no borrower-paid discount points are charged and no lender credit is given on that option. Anything below par means you are paying discount points to buy the rate down; anything above par returns a lender credit toward your closing costs.",
  },
  {
    q: "Do I need to provide tax returns to get this pricing?",
    a: "No. DSCR pricing is based on the property's rental cash flow, leverage, credit tier, and property characteristics — not personal income documents. These are business-purpose loans for non-owner-occupied property.",
  },
  {
    q: "Can I lock one of these rates from this page?",
    a: "No. A lock requires a submitted application, credit review, and property documentation. Use the scenario summary request and a loan officer will return written pricing.",
  },
  {
    q: "Why do investment property rates run higher than owner-occupied rates?",
    a: "Non-owner-occupied loans carry higher risk-based pricing adjustments because investment properties statistically default and go into foreclosure at higher rates than primary residences. This is reflected across the lending market generally, not something specific to any one pricing source.",
  },
  {
    q: "Does loan amount affect the rate I am shown?",
    a: "Yes. Pricing adjustors apply for loan amounts below and above certain thresholds, and property type, number of units, and state can also shift the price. The scenario summary reflects the exact loan amount and property profile entered.",
  },
  {
    q: "How long is a quoted investment property rate good for?",
    a: "Indicative pricing on this page reflects the moment it was priced and can move intraday with secondary-market conditions. A rate is only protected once you submit an application and receive a formal lock confirmation.",
  },
  {
    q: "Can a DSCR of less than 1.0x still qualify for financing?",
    a: "Some programs allow it at reduced leverage or with a stronger credit tier and larger reserves, though pricing is generally less favorable than at 1.0x or higher DSCR. Whether a below-1.0x scenario is viable depends on the full underwriting picture.",
  },
  {
    q: "Are these the same rates for a purchase and a cash-out refinance?",
    a: "No. Purchase, rate-and-term refinance, and cash-out refinance are priced as separate loan purposes because they carry different risk profiles, and cash-out in particular typically carries additional pricing adjustments. Select the correct purpose when reviewing your scenario.",
  },
  {
    q: "Do short-term rental properties price the same as long-term rentals?",
    a: "Not always. Some programs apply a specific adjustment or use a different qualifying income method for short-term rental properties, which can shift both eligibility and pricing versus a standard long-term lease. Confirm which method applies to your property type before comparing rates.",
  },
];

export const Route = createFileRoute("/investment-property-rates")({
  loader: async () => {
    try {
      return (await getDscrRateSnapshot({
        data: { key: scenarioKey(DEFAULT_RATE_SCENARIO) },
      })) as PublicRateResponse;
    } catch {
      return null;
    }
  },
  head: () =>
    buildHead({
      title: "Investment Property Rates — Live DSCR Pricing & APR",
      description:
        "Live DSCR investment property rates with APR, borrower points versus lender credits, estimated payment, and lock period — priced for your own scenario in seconds.",
      url: URL,
      keywords: KEYWORDS,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Rates", url: "/rates" },
        { name: "Investment Property Rates", url: URL },
      ],
      service: { name: "DSCR Investment Property Rate Center" },
      faqs: FAQS,
    }),
  component: RateCenterPage,
});

function RateCenterPage() {
  const snapshot = Route.useLoaderData();
  const initial: PublicRateResponse =
    snapshot ??
    emptyRateResponse(
      DEFAULT_RATE_SCENARIO,
      "Live pricing has not been published for the sample scenario yet. Enter your scenario and price it now.",
    );


  return (
    <SiteShell hideBreadcrumbs productLabel="Investment Property Rates">
      <PageHero
        eyebrow="Rates · DSCR"
        title="Investment property rates"
        subtitle="Live DSCR pricing for non-owner-occupied rentals — rate, APR, borrower points or lender credit, estimated payment, and lock period. Change the scenario and reprice as often as you like."
        primaryHref="/apply?product=dscr"
        image={{
          src: investmentPropertyRatesHero,
          alt: "Investment property rate calculator displayed on a desktop computer in a professional office",
        }}
      />
      <PageEssentials />
      <Breadcrumbs
        items={[{ name: "Home", href: "/" }, { name: "Rates", href: "/rates" }, { name: "Investment Property Rates" }]}
      />

      <Section>
        <DscrRateCenter initial={initial} />
      </Section>

      <Section id="how-to-compare">
        <SectionHeader
          eyebrow="How to compare"
          title={RATE_EXPLAINER_HEADING}
          intro={RATE_EXPLAINER_INTRO}
        />
        <RateComparisonExplainer className="mt-8" />
      </Section>


      <Section id="rate-definitions">
        <SectionHeader
          eyebrow="Understand the numbers"
          title="How to compare DSCR rates, and what moves them"
          intro="Short, plain-English answers to the questions investors ask most when comparing live DSCR pricing."
        />
        <div className="mt-8">
          <RateEducation />
        </div>
      </Section>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>


      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Investment property rate questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
      </Section>

      <Section>
        <div className="rounded-2xl border border-border bg-muted/40 p-6 text-xs leading-relaxed text-muted-foreground">
          <div className="font-semibold text-foreground">Rate disclosure</div>
          <p className="mt-2">
            Rates, APRs, prices, points, credits, payments, and lock periods shown on this page are live
            indications for the scenario displayed at the time stamped above. They change throughout the day
            and are subject to change without notice. Nothing on this page is a rate lock, a loan approval,
            a commitment to lend, or an offer of credit. Actual terms depend on a complete application,
            credit review, property valuation, entity documentation, and lender underwriting approval.
            Estimated principal and interest excludes property taxes, insurance, HOA dues, and third-party
            closing costs, so your total monthly payment will be higher.
          </p>
          <p className="mt-2">
            All programs shown are business-purpose loans secured by non-owner-occupied investment
            property. They are not available for owner-occupied or consumer-purpose transactions.
            Lender identities are not displayed; options are labeled anonymously.
          </p>
          <LicensingDisclosure className="mt-4" headingClassName="text-foreground" />
        </div>
      </Section>

      <CTABand
        title="Get your DSCR pricing in writing"
        body="Send the address, rent, and price. A loan officer returns written pricing for your scenario."
        href="/apply?product=dscr"
      />
    <PageOutro />
      </SiteShell>
  );
}
